<!-- src/components/Analytics/AnalyticsUsersSection.vue -->
<template>
  <div class="space-y-8 text-left font-sans">
    
    <!-- Header Block -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5">
      <div class="flex items-center gap-3">
        <div class="bg-[#20FCF1] w-2.5 h-8 rounded-full"></div>
        <div>
          <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-none">Workforce Demographics</h2>
          <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5">Registered Personnel and Access Registry</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <span class="px-3 py-1 bg-[#0A1630]/5 text-[#0A1630] text-[9px] font-black uppercase tracking-widest rounded-full">
          Identity Services Active
        </span>
      </div>
    </div>

    <!-- Executive KPI Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      
      <!-- Registered Personnel -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Total Registered</p>
            <p v-if="loadingUsers" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ totalCount }}</p>
          </div>
          <div class="p-3 bg-[#0A1630]/5 rounded-2xl text-[#0A1630]">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2 text-[9px] text-slate-400 font-black uppercase tracking-wider">
          <span class="text-emerald-500">●</span> Database Headcount
        </div>
      </div>

      <!-- Active Staff -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Active Staff</p>
            <p v-if="loadingUsers" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ activeCount }}</p>
          </div>
          <div class="p-3 bg-emerald-50 rounded-2xl text-emerald-500">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
        <div class="mt-4">
          <div class="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
            <div class="bg-[#20FCF1] h-full transition-all duration-500" :style="{ width: activePercentage + '%' }"></div>
          </div>
          <div class="flex justify-between mt-2 text-[8px] font-black uppercase text-slate-400 tracking-wider">
            <span>{{ activePercentage }}% Operational</span>
            <span>Target: 100%</span>
          </div>
        </div>
      </div>

      <!-- Pending Compliance -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Pending Compliance</p>
            <p v-if="loadingUsers" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ pendingCount }}</p>
          </div>
          <div class="p-3 bg-amber-50 rounded-2xl text-amber-500">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2 text-[9px] text-slate-400 font-black uppercase tracking-wider">
          <span class="text-amber-500">●</span> Verification Queue
        </div>
      </div>

      <!-- Compliance Rating -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Compliance Score</p>
            <p class="text-3xl font-black text-[#0A1630] leading-none">{{ ratingScore }}</p>
          </div>
          <div class="p-3 bg-indigo-50 rounded-2xl text-indigo-500">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.907c.961 0 1.36 1.243.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.384-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-1.5 text-[9px] text-slate-400 font-black uppercase tracking-wider">
          <div class="flex text-amber-400">
            <span>★</span><span>★</span><span>★</span><span>★</span><span class="text-slate-200">★</span>
          </div>
          <span>Performance Rating</span>
        </div>
      </div>
    </div>

    <!-- Role Composition Visualisation Grid -->
    <div class="bg-slate-50 p-6 md:p-8 rounded-[40px] border border-slate-100 text-left">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Role Composition Visualisation</h3>
          <p class="text-[9px] text-slate-400 mt-2 font-bold leading-none">Matrix representing workforce system access scopes and counts</p>
        </div>
        <span class="px-4 py-1.5 bg-[#0A1630]/5 text-[#0A1630] text-[9px] font-black uppercase tracking-widest rounded-full self-start sm:self-auto">
          {{ userRolesKpis.length }} Unique Roles
        </span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
        <div 
          v-for="role in userRolesKpis" 
          :key="role.name" 
          class="bg-white p-5 rounded-2xl border border-slate-100 hover:shadow-sm transition-all duration-200 flex flex-col justify-center"
        >
          <div class="flex justify-between items-center mb-2">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-tight">{{ role.name }}</span>
            <span class="text-xs font-black text-[#0A1630] bg-[#20FCF1]/10 px-2.5 py-1 rounded-lg">
              {{ role.count }}
            </span>
          </div>
          <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div class="bg-[#20FCF1] h-full transition-all duration-500" :style="{ width: getRolePercentage(role.count) + '%' }"></div>
          </div>
          <div class="flex justify-between mt-2 text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">
            <span>Workforce Representation</span>
            <span>{{ getRolePercentage(role.count) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 📊 INTERACTIVE WORKFORCE DRILL-DOWN ANALYTICS PANEL -->
    <div class="bg-white p-6 md:p-8 rounded-[40px] border border-slate-100 text-left space-y-8">
      
      <!-- Panel Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-50 pb-4">
        <div>
          <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Workforce Analytics Query Panel</h3>
          <p class="text-[9px] text-slate-400 mt-2 font-bold leading-none">Perform live, relational cascading queries to isolate specific personnel demographics</p>
        </div>
        <span class="px-3.5 py-1.5 bg-[#20FCF1]/10 text-[#0A1630] text-[9px] font-black uppercase tracking-widest rounded-full self-start sm:self-auto">
          Relational Mode Enabled
        </span>
      </div>

      <!-- Relational Filter Toolbar (CASCADING dropdown fields) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-slate-50 p-5 rounded-3xl border border-slate-100">
        
        <!-- Dropdown 1: Department -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">1. Department</label>
          <select v-model="filters.department" class="filter-select-light" @change="resetLowerFilters(1)">
            <option value="all">All Departments</option>
            <option v-for="dept in availableDepartments" :key="dept" :value="dept">
              {{ formatIdentifier(dept) }}
            </option>
          </select>
        </div>

        <!-- Dropdown 2: Employment Type (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">2. Employment Type</label>
          <select v-model="filters.employmentType" :disabled="filters.department === 'all'" class="filter-select-light" @change="resetLowerFilters(2)">
            <option value="all">All Types</option>
            <option v-for="t in availableEmploymentTypes" :key="t" :value="t">
              {{ formatIdentifier(t) }}
            </option>
          </select>
        </div>

        <!-- Dropdown 3: Sponsorship Type (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">3. Sponsorship</label>
          <select v-model="filters.sponsorshipType" :disabled="filters.employmentType === 'all'" class="filter-select-light" @change="resetLowerFilters(3)">
            <option value="all">All Sponsorships</option>
            <option v-for="s in availableSponsorshipTypes" :key="s" :value="s">
              {{ formatIdentifier(s) }}
            </option>
          </select>
        </div>

        <!-- Dropdown 4: Access Role Scope (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">4. Access Role Scope</label>
          <select v-model="filters.role" :disabled="filters.sponsorshipType === 'all'" class="filter-select-light">
            <option value="all">All Roles</option>
            <option v-for="r in availableRoles" :key="r" :value="r">
              {{ formatIdentifier(r) }}
            </option>
          </select>
        </div>

      </div>

      <!-- Temporal Period RANGE SLIDER Control -->
      <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
        <div class="flex justify-between items-center">
          <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">Adjust Query Registration Window</span>
          <span class="px-3 py-1 bg-[#0A1630] text-[#20FCF1] text-[9px] font-black uppercase tracking-wider rounded-lg leading-none">
            Selected: {{ sliderLabel }}
          </span>
        </div>
        
        <div class="px-4">
          <input 
            type="range" 
            min="0" 
            max="5" 
            v-model.number="sliderIndex" 
            class="w-full accent-[#0A1630] cursor-pointer"
          />
          <div class="flex justify-between mt-2 text-[8px] font-black text-slate-400 uppercase tracking-widest px-1">
            <span>Past Month</span>
            <span>Past Quarter</span>
            <span>6 Months</span>
            <span>3 Quarters</span>
            <span>Past Year</span>
            <span>All Time</span>
          </div>
        </div>
      </div>

      <!-- Spotlight Summary query results cards -->
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6 bg-[#0A1630] text-white p-8 rounded-[40px] border-b-4 border-[#20FCF1] relative overflow-hidden">
        <div class="md:col-span-4 flex flex-col justify-center items-center md:items-start text-center md:text-left border-b md:border-b-0 md:border-r border-white/10 pb-6 md:pb-0 md:pr-8 shrink-0">
          <p class="text-[9px] font-black uppercase text-[#20FCF1] tracking-[0.2em] mb-2">Spotlight Results</p>
          <div class="flex items-baseline gap-2">
            <h4 class="text-6xl font-black text-[#20FCF1] tracking-tight leading-none">
              {{ filteredRecords.length }}
            </h4>
            <span class="text-xs font-black uppercase tracking-widest text-white">Staff</span>
          </div>
          <p class="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-3">Matching personnel profiles in timeline</p>
        </div>

        <div class="md:col-span-8 flex flex-col justify-between space-y-4 text-left md:pl-6">
          <div>
            <h4 class="text-sm font-black uppercase text-[#20FCF1] tracking-wider leading-none mb-3">Downstream Demographics Insight</h4>
            <p class="text-xs font-semibold text-slate-300 leading-relaxed">
              {{ demographicsInsightText }}
            </p>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-white/5">
            <div>
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Status Ratios</span>
              <span class="text-[10px] font-black text-emerald-400 uppercase tracking-tight leading-none block">
                {{ statusBreakdown.active }} Active
              </span>
              <span class="text-[10px] font-black text-amber-400 uppercase tracking-tight leading-none block mt-1">
                {{ statusBreakdown.pending }} Pending Onboarding
              </span>
            </div>
            <div>
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Access Levels</span>
              <span class="text-[10px] font-black text-indigo-400 uppercase tracking-tight leading-none block">
                {{ roleBreakdown.standard }} Standard Staff
              </span>
              <span class="text-[10px] font-black text-orange-400 uppercase tracking-tight leading-none block mt-1">
                {{ roleBreakdown.manager }} Management
              </span>
            </div>
            <div class="col-span-2 sm:col-span-1">
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Current Focus</span>
              <span class="text-[10px] font-black text-slate-200 uppercase tracking-tight leading-none block">
                {{ currentQueryFocus }}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { firebaseApp } from '@/firebaseConfig';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  }
});

const userStore = useUserStore();
const db = getFirestore(firebaseApp);

const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

const usersList = ref([]);
const loadingUsers = ref(false);

const filters = reactive({
  department: 'all',
  employmentType: 'all',
  sponsorshipType: 'all',
  role: 'all'
});

const sliderIndex = ref(5);
const sliderLabels = ['Past Month', 'Past Quarter', 'Past 6 Months', 'Past 3 Quarters', 'Past Year', 'All Time'];
const sliderLabel = computed(() => sliderLabels[sliderIndex.value]);

// Dynamic temporal threshold calculation linked to slider select index
const dateThreshold = computed(() => {
  const now = new Date();
  if (sliderIndex.value === 0) return new Date(now.setMonth(now.getMonth() - 1));
  if (sliderIndex.value === 1) return new Date(now.setMonth(now.getMonth() - 3));
  if (sliderIndex.value === 2) return new Date(now.setMonth(now.getMonth() - 6));
  if (sliderIndex.value === 3) return new Date(now.setMonth(now.getMonth() - 9));
  if (sliderIndex.value === 4) return new Date(now.setFullYear(now.getFullYear() - 1));
  return null;
});

// Resets lower downstream fields when higher cascading selections change
const resetLowerFilters = (stage) => {
  if (stage === 1) {
    filters.employmentType = 'all';
    filters.sponsorshipType = 'all';
    filters.role = 'all';
  } else if (stage === 2) {
    filters.sponsorshipType = 'all';
    filters.role = 'all';
  } else if (stage === 3) {
    filters.role = 'all';
  }
};

// Queries the live Firestore userProfiles collection dynamically based on client context
const fetchLiveUsers = async () => {
  const clientId = currentClientId.value;
  if (!clientId) return;
  loadingUsers.value = true;
  try {
    const q = query(collection(db, 'userProfiles'), where('clientId', '==', clientId));
    const querySnapshot = await getDocs(q);
    const temp = [];
    querySnapshot.forEach((doc) => {
      const d = doc.data();
      
      // Case-insensitive status mapping to prevent active state colors from mismatching
      let uStatus = d.status || 'Active';
      const lowerStatus = uStatus.toLowerCase().trim();

      if (lowerStatus === 'active') {
        uStatus = 'Active';
      } else if (lowerStatus === 'pending review') {
        uStatus = 'Pending Review';
      } else if (lowerStatus === 'pending onboarding' || lowerStatus === 'pending') {
        uStatus = 'Pending Onboarding';
      } else if (d.onboarding_yourDetails === false) {
        uStatus = 'Pending Onboarding';
      } else {
        uStatus = uStatus.charAt(0).toUpperCase() + uStatus.slice(1);
      }

      let uDate = '';
      if (d.createdAt) {
        if (d.createdAt.toDate) {
          uDate = d.createdAt.toDate().toISOString();
        } else if (typeof d.createdAt === 'string') {
          uDate = d.createdAt;
        } else if (d.createdAt.seconds) {
          uDate = new Date(d.createdAt.seconds * 1000).toISOString();
        }
      }
      
      temp.push({
        id: doc.id,
        name: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.knownAs || d.userName || 'Unknown',
        email: d.email || '',
        role: d.role || d.jobTitle || 'Standard',
        status: uStatus,
        rating: d.averageRating !== undefined && d.averageRating !== null && d.averageRating > 0 
          ? Number(d.averageRating).toFixed(1) 
          : 'No Index',
        department: d.department || d.departments?.[0] || 'General',
        employmentType: d.employmentType || 'Employee',
        sponsorshipType: d.sponsorshipType || 'N/A',
        createdAt: uDate
      });
    });
    usersList.value = temp;
  } catch (err) {
    console.error("Failed to query live userProfiles from Firestore:", err);
  } finally {
    loadingUsers.value = false;
  }
};

watch(currentClientId, (newId) => {
  if (newId) {
    fetchLiveUsers();
  }
}, { immediate: true });

// Dynamic cascading options based on high-level choices
const availableDepartments = computed(() => {
  const set = new Set();
  usersList.value.forEach(u => { if (u.department) set.add(u.department); });
  return Array.from(set);
});

const availableEmploymentTypes = computed(() => {
  if (filters.department === 'all') return [];
  const set = new Set();
  usersList.value
    .filter(u => u.department === filters.department)
    .forEach(u => { if (u.employmentType) set.add(u.employmentType); });
  return Array.from(set);
});

const availableSponsorshipTypes = computed(() => {
  if (filters.employmentType === 'all') return [];
  const set = new Set();
  usersList.value
    .filter(u => u.department === filters.department && u.employmentType === filters.employmentType)
    .forEach(u => { if (u.sponsorshipType) set.add(u.sponsorshipType); });
  return Array.from(set);
});

const availableRoles = computed(() => {
  if (filters.sponsorshipType === 'all') return [];
  const set = new Set();
  usersList.value
    .filter(u => u.department === filters.department && u.employmentType === filters.employmentType && u.sponsorshipType === filters.sponsorshipType)
    .forEach(u => { if (u.role) set.add(u.role); });
  return Array.from(set);
});

// Reactive cascading record filter evaluation
const filteredRecords = computed(() => {
  return usersList.value.filter(user => {
    const matchesDept = filters.department === 'all' || user.department === filters.department;
    const matchesEmp = filters.employmentType === 'all' || user.employmentType === filters.employmentType;
    const matchesSponsor = filters.sponsorshipType === 'all' || user.sponsorshipType === filters.sponsorshipType;
    const matchesRole = filters.role === 'all' || user.role === filters.role;
    const matchesDate = !dateThreshold.value || (user.createdAt && new Date(user.createdAt) >= dateThreshold.value);
    
    return matchesDept && matchesEmp && matchesSponsor && matchesRole && matchesDate;
  });
});

const statusBreakdown = computed(() => {
  const records = filteredRecords.value;
  const active = records.filter(r => r.status === 'Active').length;
  return {
    active,
    pending: records.length - active
  };
});

const roleBreakdown = computed(() => {
  const records = filteredRecords.value;
  const manager = records.filter(r => r.role.toLowerCase().includes('manager') || r.role.toLowerCase().includes('admin') || r.role.toLowerCase().includes('director')).length;
  return {
    manager,
    standard: records.length - manager
  };
});

const currentQueryFocus = computed(() => {
  if (filters.role !== 'all') return formatIdentifier(filters.role);
  if (filters.sponsorshipType !== 'all') return formatIdentifier(filters.sponsorshipType);
  if (filters.employmentType !== 'all') return formatIdentifier(filters.employmentType);
  if (filters.department !== 'all') return formatIdentifier(filters.department);
  return 'Global Demographics';
});

// Relational insight generator detailing dynamic results of queries
const demographicsInsightText = computed(() => {
  const count = filteredRecords.value.length;
  const period = sliderLabel.value.toLowerCase();
  
  if (count === 0) {
    return `No active workforce profiles match the selected cascading parameters registered during the ${period}. Demographic distributions remain stable.`;
  }
  
  let desc = `Drill-down identifies exactly ${count} registered staff profile${count > 1 ? 's' : ''} mapped in the ${period}. `;
  if (filters.department !== 'all') {
    desc += `These personnel operate strictly inside the ${formatIdentifier(filters.department)} department. `;
  }
  if (filters.employmentType !== 'all') {
    desc += `Operational parameters classify them as ${formatIdentifier(filters.employmentType)} accounts. `;
  }
  if (statusBreakdown.value.pending > 0) {
    desc += `Currently, ${statusBreakdown.value.pending} of these profiles remain in the onboarding verification backlog.`;
  } else {
    desc += `All matching profiles are verified and active in the field.`;
  }
  return desc;
});

const totalCount = computed(() => {
  if (usersList.value.length > 0) return usersList.value.length;
  const total = props.data?.totalUsers ?? props.data?.total ?? 170;
  return isNaN(Number(total)) ? 0 : Number(total);
});

const activeCount = computed(() => {
  if (usersList.value.length > 0) {
    return usersList.value.filter(u => u.status === 'Active').length;
  }
  const active = props.data?.totalActive ?? props.data?.total ?? 154;
  return isNaN(Number(active)) ? 0 : Number(active);
});

const pendingCount = computed(() => {
  if (usersList.value.length > 0) {
    return usersList.value.filter(u => u.status === 'Pending Onboarding').length;
  }
  const count = props.data?.totalPending ?? 29;
  return isNaN(Number(count)) ? 0 : Number(count);
});

const ratingScore = computed(() => {
  const reviewedUsers = usersList.value.filter(u => u.rating !== 'No Index' && !isNaN(Number(u.rating)));
  if (reviewedUsers.length > 0) {
    const sum = reviewedUsers.reduce((acc, u) => acc + Number(u.rating), 0);
    return (sum / reviewedUsers.length).toFixed(1);
  }
  return '5.0';
});

const activePercentage = computed(() => {
  const total = totalCount.value;
  if (!total) return 0;
  return Math.round((activeCount.value / total) * 100);
});

// Sort roles by highest headcount first
const userRolesKpis = computed(() => {
  if (usersList.value.length > 0) {
    const map = {};
    usersList.value.forEach(u => {
      map[u.role] = (map[u.role] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }
  const rolesData = props.data?.roleBreakdown ?? props.data?.byRole ?? {};
  if (Object.keys(rolesData).length === 0) return [];
  return Object.entries(rolesData)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
});

const getRolePercentage = (count) => {
  const total = totalCount.value;
  if (!total) return 0;
  return Math.round((count / total) * 100);
};

const formatIdentifier = (id) => {
  if (!id) return 'Unknown';
  return id
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};
</script>

<style scoped>
.filter-input-light {
  @apply w-full bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0A1630] focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all;
}
.filter-select-light {
  @apply w-full bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0A1630] focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%230a1630'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 0.8rem;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #20FCF1;
  border-radius: 10px;
}
</style>